import React from 'react';

import { ErrorBoundary, type ErrorBoundaryProps } from 'react-error-boundary';

import { fg } from '@atlaskit/platform-feature-flags';
import { NavErrorBoundary } from '@atlassian/navigation-error-boundary';
import type { NavigationExperienceIdEnum } from '@atlassian/navigation-perf-utils';

import { useMenuControl, useMenuStateContext } from '../../context';

interface GenericErrorBoundaryProps {
	/**
	 * Optional app name to help identify the app where the error occurred when context is not available.
	 */
	app?: string;
	children: ErrorBoundaryProps['children'];
	fallback?: ErrorBoundaryProps['fallback'];
	/**
	 * The interaction ID to help identify the experience where the error occurred.
	 */
	interactionId?: NavigationExperienceIdEnum;
	/**
	 * The name of the component using this error boundary to help identify where errors occur.
	 */
	name: string;
	onError?: (error: Error) => void;

	/**
	 * When true, the error boundary will not render any error UI (only applicable for NavErrorBoundary).
	 * Defaults to true.
	 */
	silent?: boolean;
}

export function GenericErrorBoundary({
	children,
	name,
	interactionId = undefined,
	fallback = null,
	onError = undefined,
	silent = true,
	app = undefined,
}: GenericErrorBoundaryProps) {
	const { product } = useMenuStateContext();
	const { product: productV1 } = useMenuControl();
	const tags = {
		// use combined check for now, will consolidate menu state and menu control contexts next https://hello.jira.atlassian.cloud/browse/NAVX-3349
		app: app || product || productV1,
	};
	return fg('navx_uc_sentry_enabled') ? (
		<NavErrorBoundary
			silent={silent}
			disableAnalytics // disable analytics for now
			location={name}
			onError={onError}
			sentryConfig={{ product: 'universal-create' }}
			interactionId={interactionId}
			tags={tags}
		>
			{children}
		</NavErrorBoundary>
	) : (
		<ErrorBoundary fallback={fallback} onError={onError}>
			{children}
		</ErrorBoundary>
	);
}
